import {Platform} from 'react-native';
import {AppActions, AppThunk} from '..';
import {storage, StorageKeys} from '../../../shared/storage';
import {profileSlice} from '../../reducers/profile';
import {httpService, HttpWrapperOptions} from '../../../shared/httpWrapper';
import {apiUrls} from '../../../shared/constants/urls';
import {hashPassword} from '../../../shared/pipes';
import {loginSlice} from '../../reducers/login';
import {setRuntimeLoginStepState} from '../runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {getApiRoute} from '../../../shared/pipes';
import {
  LoginResponsePayload,
  AuthorizeResponsePayload,
  AuthorizeCabinetRequestPayload,
  SendCodeRequestPayload,
  SendCodeResponsePayload,
  SendCodeWithCheckPhoneResponsePayload,
  SetPasswordRequestPayload,
  SendCodeChannelType,
  RequestedOperationType,
  CheckCodeRequestPayload,
  SignOutRequestPayload,
} from '../../../shared/models/api';
import {onlyDigitsPipe} from '../../../shared/pipes';
import {fcmService} from '../../../shared/fcmWrapper';
import {getTokenResponse} from '../../../shared/fcmWrapper/model';
import {Settings} from '../../../shared/constants/config';
import {contentSlice} from '../../reducers/resources/content';
import {setBadgeCount} from '../../../shared/fcmWrapper/pushNotification';

export const setLoginResponse = (response: LoginResponsePayload): AppThunk => {
  return (dispatch) => {
    dispatch(loginSlice.actions.setLoginResponse(response));
  };
};

export const setLoginValue = (login: string): AppThunk => {
  return (dispatch) => {
    dispatch(loginSlice.actions.setLogin(login));
  };
};

export const checkLogin = (login: string): AppThunk => {
  return async (dispatch) => {
    let response: LoginResponsePayload | null = null;
    response = await httpService.get<LoginResponsePayload>(
      getApiRoute(apiUrls.login, {login: login || ''}),
      new HttpWrapperOptions().Errors(false).ThrowException(true).Wait(true),
    );
    dispatch(loginSlice.actions.setLogin(login));
    dispatch(setLoginResponse(response));
    dispatch(loginSlice.actions.setLoginResponse(response));
    dispatch(loginSlice.actions.setSendCodeResponse(null));
  };
};

export const login = (password: string): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const authResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.auth),
      {
        login: onlyDigitsPipe(state.login.login ?? ''),
        loginType: state.login.loginResponse?.loginType,
        passHash,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
      } as AuthorizeCabinetRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (authResponse) {
      await storage.setItem(StorageKeys.token, authResponse.token);
      await storage.setItem(StorageKeys.transportCardToken, authResponse.transportCardApiToken);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(authResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const registration = (password: string, code: string): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passwordHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const newPasswordResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.newPass),
      {
        passwordHash,
        code,
        codeId: state.login.sendCodeResponse?.codeId,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
      } as SetPasswordRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (newPasswordResponse) {
      await storage.setItem(StorageKeys.token, newPasswordResponse.token);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(newPasswordResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const createNewPassword = (code: string, password: string, isNewPhoneRegistration: boolean): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passwordHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const newPasswordResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.newPass),
      {
        passwordHash,
        code,
        codeId: state.login.sendCodeResponse?.codeId,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
        requestedOperation: isNewPhoneRegistration
          ? RequestedOperationType.SetPhoneNumberConfirmation
          : RequestedOperationType.SetPasswordConfirmation,
      } as SetPasswordRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (newPasswordResponse) {
      await storage.setItem(StorageKeys.token, newPasswordResponse.token);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(newPasswordResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const createNewPinCode = (pinCode: string): AppThunk => {
  return async (dispatch) => {
    await storage.setItem(StorageKeys.pinCode, pinCode);
    dispatch(profileSlice.actions.setPinCode(pinCode));
  };
};

export const logout = (): AppThunk => {
  return async (dispatch) => {
    let fcmToken: string | null = null;
    try {
      fcmToken = (await fcmService.getToken()).token;
    } catch {
      fcmToken = await storage.getItem<string>(StorageKeys.fcmToken);
    }

    if (fcmToken) {
      await httpService.post<void>(getApiRoute(apiUrls.signOut), {
        firebaseId: fcmToken,
      } as SignOutRequestPayload);
    }

    await fcmService.cleanToken();

    dispatch({type: AppActions.Reset});
    dispatch(profileSlice.actions.reset(null));
    await storage.removeItem(StorageKeys.pinCode);
    dispatch(profileSlice.actions.setSessionState(false));
    await storage.removeItem(StorageKeys.token);
    await dispatch(contentSlice.actions.reset(null));
    setBadgeCount(0);
  };
};

export const enter = (token: string): AppThunk => {
  return async (dispatch) => {
    dispatch(profileSlice.actions.setToken(token));
  };
};

export const resetLoginState = (): AppThunk => {
  return async (dispatch) => {
    dispatch(loginSlice.actions.reset(null));
  };
};

export const getNewCode = (numCardESK: string, showStandardError: boolean, channel: SendCodeChannelType): AppThunk => {
  return async (dispatch) => {
    const response = await httpService.post<SendCodeResponsePayload>(
      getApiRoute(apiUrls.sendCode),
      {
        channel,
        requestedOperation: RequestedOperationType.SetPasswordConfirmation,
        numCardESK,
      } as SendCodeRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(showStandardError).ThrowException(true),
    );
    if (response) {
      dispatch(loginSlice.actions.setSendCodeResponse(response));
      if (response?.codeId && channel === SendCodeChannelType.Email) {
        await storage.setItem(StorageKeys.recoveryCodeId, response?.codeId);
      }
    }
  };
};

export const sendCodeWithCheckPhone = (
  cardNum: string,
  snils: string,
  mobilePhone: string,
  showStandardError: boolean,
): AppThunk => {
  return async (dispatch) => {
    const response = await httpService.post<SendCodeWithCheckPhoneResponsePayload>(
      getApiRoute(apiUrls.sendCodeWithCheckPhone),
      {
        cardNum,
        snils,
        mobilePhone,
      } as SendCodeWithCheckPhoneResponsePayload,
      new HttpWrapperOptions().Wait(true).Errors(showStandardError).ThrowException(true),
    );
    if (response && response.codeId) {
      dispatch(loginSlice.actions.setSendCodeResponse(response));
    }
  };
};

export const checkCode = (code: string, mobilePhone: string, requestedOperation: RequestedOperationType): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    await httpService.post(
      getApiRoute(apiUrls.checkCode),
      {
        codeId: state.login.sendCodeResponse?.codeId,
        code,
        requestedOperation,
        mobilePhone,
      } as CheckCodeRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(false).ThrowException(true),
    );
  };
};
